import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Button from '../../Common/Button';
import { CsvToIntArray } from '../../../helpers/strings';
import { getMilestoneIds } from '../../../helpers/reports';
import '../Reports.scss';

export class LocationSummaryPerProject extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            locationKeys: [],
            projectId: '',
            milestoneIds: '',
            continentIds: '',
            areaIds: '',
            regionIds: '',
            brandIds: '',
            managementCompanyIds: '',
            locationTypeIds: '',
            columnKeys: '',
            countryIds: '',
            communityIds: ''
        }

        this.state = {
            showMoreFields: false,
            emptyForm: initialFormSelections,
            locationKeys: initialFormSelections.locationKeys,
            projectId: initialFormSelections.projectId,
            milestoneIds: initialFormSelections.milestoneIds,
            continentIds: initialFormSelections.continentIds,
            areaIds: initialFormSelections.areaIds,
            regionIds: initialFormSelections.regionIds,
            brandIds: initialFormSelections.brandIds,
            managementCompanyIds: initialFormSelections.managementCompanyIds,
            locationTypeIds: initialFormSelections.locationTypeIds,
            columnOptions: [
                { id: 'Area', name: 'Area' }, 
                { id: 'Brand', name: 'Brand' }, 
                { id: 'Continent', name: 'Continent' }, 
                { id: 'Country', name: 'Country' },
                { id: 'LocationType', name: 'Location Type' }, 
                { id: 'ManagementCompany', name: 'Management Company' },
                { id: 'Region', name: 'Region' }
            ],
            columnKeys: initialFormSelections.columnKeys,
            countryIds: initialFormSelections.countryIds,
            communityIds: initialFormSelections.communityIds
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,
                locationKeys: appState.locationKeys ? appState.locationKeys : newState.locationKeys,
                projectId: appState.projectId ? appState.projectId : newState.projectId,
                milestoneIds: appState.milestoneIds ? appState.milestoneIds : newState.milestoneIds,
                continentIds: appState.continentIds ? appState.continentIds : newState.continentIds,
                areaIds: appState.areaIds ? appState.areaIds : newState.areaIds,
                regionIds: appState.regionIds ? appState.regionIds : newState.regionIds,
                brandIds: appState.brandIds ? appState.brandIds : newState.brandIds,
                managementCompanyIds: appState.managementCompanyIds ? appState.managementCompanyIds : newState.managementCompanyIds,
                locationTypeIds: appState.locationTypeIds ? appState.locationTypeIds : newState.locationTypeIds,
                columnKeys: appState.columnKeys ? appState.columnKeys : newState.columnKeys,
                countryIds: appState.countryIds ? appState.countryIds : newState.countryIds,
                communityIds: appState.communityIds ? appState.communityIds : newState.communityIds
            }
        }

        // Possibly override app state values with default values or URL params
        let locationKeys = this.getDefaultLocationKeys(newState.locationKeys);
        let projectId = this.getDefaultProjectId(newState.projectId);
        let milestoneIds = this.getDefaultMilestone(newState.milestoneIds);
        let communityIds = this.getDefaultCommunityIds(newState.communityIds);

        newState = {
            ...newState,
            locationKeys: locationKeys,
            projectId: projectId,
            milestoneIds: milestoneIds,
            communityIds: communityIds
        }

        this.setState({
            ...this.state,
            ...newState
        });
    }

    getDefaultCommunityIds = (savedCommunityIds) => {
        let communityIds = '';
        const urlCommunityIds = this.props.urlParams.communityIds;
        if (urlCommunityIds && urlCommunityIds.trim().length > 0) {
            communityIds = urlCommunityIds;
        } else if (savedCommunityIds) {
            communityIds = savedCommunityIds;
        }

        return communityIds;
    }

    getDefaultMilestone = (savedMilestoneIds) => {
        /**
         * The isMilestone URL param optionally sets one of the values in the
         * hard-coded milestone options list.  0 = Non-milestones, 1 = Milestones.
         * If no URL params have been set, we fallback to any saved milestone Ids
         * that were previously selected.
         */
        let milestoneIds = '';
        const urlIsMilestone = this.props.urlParams.isMilestone;
        if (urlIsMilestone && urlIsMilestone.trim().length > 0) {
            milestoneIds = urlIsMilestone;
        } else if (savedMilestoneIds) {
            milestoneIds = savedMilestoneIds;
        }

        return milestoneIds;
    }

    getDefaultProjectId = (savedProjectId) => {
        let projectId = '';
        const urlProjectId = this.props.urlParams.projectId;
        if (urlProjectId && urlProjectId.trim().length > 0) {
            projectId = urlProjectId;
        } else if (savedProjectId) {
            projectId = savedProjectId;
        }

        return projectId;
    }

    getDefaultLocationKeys = (savedLocationKeys) => {
        /**
         * Location(s) can come from three places:
         *  1. The URL params
         *  2. Already selected Location Keys saved in app state
         *  3. The current user's default location
         *
         * When this report is called from the Task List page of the site, there is an attempt
         * to pass along any location(s) the user has already selected on that page, to auto-populate
         * the location field here.  We try to grab those values first.
         *
         * If the URL params location(s) aren't set, then we fallback to the selected location keys,
         * and if none have been selected, then we try to pull the default location from the current
         * user's default location.
         */
        let locationKeys = '';
        const urlLocationKeys = this.props.urlParams.locationKeys;
        const defaultLocationKey = this.props.filters.defaultLocationKey;
        if (urlLocationKeys && urlLocationKeys.length > 0) {
            locationKeys = urlLocationKeys.join(',');
        } else if (savedLocationKeys) {
            locationKeys = savedLocationKeys;
        } else if (defaultLocationKey && defaultLocationKey.trim().length > 0) {
            locationKeys = defaultLocationKey;
        }

        return locationKeys;
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        if (!this.state.projectId || this.state.projectId <= 0) {
            return false;
        }

        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.projectId = (reportRequest.projectId) ? reportRequest.projectId : form.projectId;
        form.locationKeys = (reportRequest.locationKeys) ? reportRequest.locationKeys.join(',') : form.locationKeys;
        form.brandIds = (reportRequest.brandIds) ? reportRequest.brandIds.join(',') : form.brandIds;
        form.regionIds = (reportRequest.regionIds) ? reportRequest.regionIds.join(',') : form.regionIds;
        form.managementCompanyIds = (reportRequest.managementCompanyIds) ? reportRequest.managementCompanyIds.join(',') : form.managementCompanyIds;
        form.columnKeys = (reportRequest.columnKeys) ? reportRequest.columnKeys.join(',') : form.columnKeys;
        form.continentIds = (reportRequest.continentIds) ? reportRequest.continentIds.join(',') : form.continentIds;
        form.areaIds = (reportRequest.areaIds) ? reportRequest.areaIds.join(',') : form.areaIds;
        form.milestoneIds = getMilestoneIds(reportRequest.isMilestone);
        form.locationTypeIds = (reportRequest.locationTypeIds) ? reportRequest.locationTypeIds.join(',') : form.locationTypeIds;
        form.countryIds = (reportRequest.countryIds) ? reportRequest.countryIds.join(',') : form.countryIds;
        form.communityIds = (reportRequest.communityIds) ? reportRequest.communityIds.join(',') : form.communityIds;

        this.setState({
            ...this.state,
            ...form
        });
    }

    mapToApiRequest = () => {
        let isMilestone = null; // default to none or both
        if (this.state.milestoneIds.length > 0) {
            const selectedMilestoneIds = this.state.milestoneIds.split(',');
            if (selectedMilestoneIds.length == 1) {
                isMilestone = selectedMilestoneIds[0] === '1' ? true : false;
            }
        }

        let locationKeys = [];
        if(this.state.locationKeys && this.state.locationKeys.length > 0){
            locationKeys = this.state.locationKeys.split(',');
        }

        let columnKeys = [];
        if(this.state.columnKeys && this.state.columnKeys.length > 0){
            columnKeys = this.state.columnKeys.split(',');
        }

        const request = {
            reportId: this.props.report.id,
            projectId: this.state.projectId,
            locationKeys: locationKeys,
            brandIds: CsvToIntArray(this.state.brandIds),
            regionIds: CsvToIntArray(this.state.regionIds),
            managementCompanyIds: CsvToIntArray(this.state.managementCompanyIds),
            columnKeys: columnKeys,
            continentIds: CsvToIntArray(this.state.continentIds),
            areaIds: CsvToIntArray(this.state.areaIds),
            isMilestone: isMilestone,
            locationTypeIds: CsvToIntArray(this.state.locationTypeIds),
            countryIds: CsvToIntArray(this.state.countryIds),
            communityIds: CsvToIntArray(this.state.communityIds)
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onToggleShowMore = () => {
        this.setState({
            showMoreFields: !this.state.showMoreFields
        });
    }

    onFieldChange = (name, value) => {
        this.setState({ ...this.state, [name]: value });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    render() {
        const showMoreText = this.state.showMoreFields ? "Show Less Filters" : "Show More Filters";
        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectId"
                            label="Project" idField="id" valueField="name"
                            options={this.props.filters.projects}
                            selectedValues={this.state.projectId.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={true}
                            errorMessage="Project is required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="columnKeys"
                            label="Display Optional Columns" idField="id" valueField="name"
                            options={this.state.columnOptions}
                            selectedValues={this.state.columnKeys.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>                    
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="locationKeys"
                            label="Location" idField="id" valueField="name"
                            options={this.props.filters.locations}
                            selectedValues={this.state.locationKeys.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="continentIds"
                            label="Continent" idField="id" valueField="name"
                            options={this.props.filters.continents}
                            selectedValues={this.state.continentIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="areaIds"
                            label="Area" idField="id" valueField="name"
                            options={this.props.filters.areas}
                            selectedValues={this.state.areaIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="regionIds"
                            label="Region" idField="id" valueField="name"
                            options={this.props.filters.regions}
                            selectedValues={this.state.regionIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                {this.state.showMoreFields &&
                    <Fragment>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="brandIds"
                                    label="Brand" idField="id" valueField="name"
                                    options={this.props.filters.brands}
                                    selectedValues={this.state.brandIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="managementCompanyIds"
                                    label="Management Company" idField="id" valueField="name"
                                    options={this.props.filters.managementCompanies}
                                    selectedValues={this.state.managementCompanyIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="locationTypeIds"
                                    label="Location Type" idField="id" valueField="name"
                                    options={this.props.filters.locationTypes}
                                    selectedValues={this.state.locationTypeIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="milestoneIds"
                                    label="Milestone" idField="id" valueField="name"
                                    options={this.props.filters.milestones}
                                    selectedValues={this.state.milestoneIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>                            
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="countryIds"
                                    label="Country" idField="id" valueField="name"
                                    options={this.props.filters.countries}
                                    selectedValues={this.state.countryIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="communityIds"
                                    label="Community" idField="id" valueField="name"
                                    options={this.props.filters.communities}
                                    selectedValues={this.state.communityIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                    </Fragment>
                }
                <div className="row text-end">
                    <div className="col-sm-12">
                        <Button name="showMore" type="button" bootstrapClass="link" text={showMoreText} onClick={this.onToggleShowMore} />
                    </div>
                </div>
            </form>
        );
    }
}

LocationSummaryPerProject.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    registerReportFilterFunctions: PropTypes.func.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired
};

export default LocationSummaryPerProject;
